import { useState } from 'react';
import { Leaf } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const bagTypes = [
  'Jute Bags',
  'Cotton Canvas Bags',
  'Pouches',
  'Wine Bags',
  'Custom Design',
];

const quantityRanges = [
  '100 - 500 units',
  '500 - 1,000 units',
  '1,000 - 5,000 units',
  '5,000 - 10,000 units',
  '10,000+ units',
];

export const RequestQuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    bagType: '',
    quantity: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch('/php/send-mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'quote',
          name: formData.name,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          bagType: formData.bagType,
          quantity: formData.quantity,
          message: formData.message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Something went wrong');
      }
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          bagType: '',
          quantity: '',
          message: '',
        });
        onClose();
      }, 2000);
    } catch (err) {
      alert(err.message || 'Failed to send. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[480px] bg-[#F5F2ED] border border-[#E3DED8] rounded-lg p-0 overflow-hidden"
        data-testid="quote-modal"
      >
        {/* Header */}
        <div className="bg-[#2F4F3E] px-5 py-4">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Leaf className="w-5 h-5 text-white" />
              <span className=" text-base text-white">Bags4Ever</span>
            </div>
            <DialogTitle className=" text-xl text-white">
              Request a Quote
            </DialogTitle>
            <DialogDescription className=" text-white/70 text-sm">
              Fill out the form below and we'll get back to you within 24 hours.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {isSuccess ? (
            <div className="text-center py-6" data-testid="success-message">
              <div className="w-14 h-14 rounded-full bg-[#2F4F3E]/10 flex items-center justify-center mx-auto mb-3">
                <Leaf className="w-7 h-7 text-[#2F4F3E]" />
              </div>
              <h3 className=" text-lg text-[#2B2B2B] mb-1">
                Thank You!
              </h3>
              <p className=" text-sm text-[#5A5A5A]">
                We've received your request and will contact you soon.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className=" text-sm text-[#2B2B2B]">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company" className=" text-sm text-[#2B2B2B]">
                    Company Name
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className=" text-sm text-[#2B2B2B]">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className=" text-sm text-[#2B2B2B]">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className=" text-sm text-[#2B2B2B]">
                    Bag Type *
                  </Label>
                  <Select
                    value={formData.bagType}
                    onValueChange={(value) => handleSelectChange('bagType', value)}
                    required
                  >
                    <SelectTrigger 
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                      data-testid="select-bag-type"
                    >
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E3DED8]">
                      {bagTypes.map((type) => (
                        <SelectItem key={type} value={type} className=" text-sm">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className=" text-sm text-[#2B2B2B]">
                    Quantity Range
                  </Label>
                  <Select
                    value={formData.quantity}
                    onValueChange={(value) => handleSelectChange('quantity', value)}
                  >
                    <SelectTrigger 
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                      data-testid="select-quantity"
                    >
                      <SelectValue placeholder="Select quantity" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E3DED8]">
                      {quantityRanges.map((range) => (
                        <SelectItem key={range} value={range} className=" text-sm">
                          {range}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message" className=" text-sm text-[#2B2B2B]">
                  Additional Details
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  rows={3}
                  className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md resize-none text-sm"
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md py-5 text-sm font-medium"
                data-testid="submit-quote-btn"
              >
                {isSubmitting ? 'Sending...' : 'Submit Request'}
              </Button>

              <p className="text-center text-xs text-[#5A5A5A]">
                By submitting, you agree to our privacy policy.
              </p>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
